import React, { useState, useEffect } from "react";
import styles from "./Footer.module.scss";
import { fetchPhones, fetchBranches } from "../../services/branchService";
import { ReactComponent as PhoneIcon } from "../../assets/icons/footer/phone_icon.svg";
import { ReactComponent as MailIcon } from "../../assets/icons/footer/mail_icon.svg";
import { ReactComponent as TelegramIcon } from "../../assets/icons/footer/telegram_icon.svg";
import { ReactComponent as MaxIcon } from "../../assets/icons/footer/max_icon.svg";
import LocationCard from "../locationCard/LocationCard";

const Footer = ({ onOpenPopup }) => {
  const [contacts, setContacts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationsError, setLocationsError] = useState(null);

  useEffect(() => {
    const loadPhones = async () => {
      try {
        const phonesData = await fetchPhones();

        const formattedContacts = phonesData
          .sort((a, b) => a.display_order - b.display_order)
          .map((phone, index) => ({
            id: index + 1,
            label: "Телефон",
            value: formatPhoneNumber(phone.phone_number),
            href: `tel:+${phone.phone_number.replace(/\D/g, "")}`,
          }));

        setContacts(formattedContacts);
        setError(null);
      } catch (err) {
        setError("Не удалось загрузить контакты");
        setContacts([]);
      }
    };

    const loadLocations = async () => {
      try {
        setLocationsLoading(true);
        const branchesData = await fetchBranches();

        const formattedLocations = branchesData
          .sort((a, b) => a.display_order - b.display_order)
          .map((branch) => ({
            id: branch.id,
            address: branch.address || branch.name,
            branchName: branch.name,
            phone: branch.phone || "",
            email: branch.email || "",
            workingHours: branch.working_hours || "",
          }));

        setLocations(formattedLocations);
        setLocationsError(null);
      } catch (err) {
        setLocationsError("Не удалось загрузить адреса филиалов");
        setLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };

    const loadAllData = async () => {
      try {
        setLoading(true);
        await Promise.all([loadPhones(), loadLocations()]);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, []);

  const formatPhoneNumber = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, "");

    if (
      cleaned.length === 11 &&
      (cleaned.startsWith("7") || cleaned.startsWith("8"))
    ) {
      return `+7-${cleaned.substring(1, 4)}-${cleaned.substring(
        4,
        7,
      )}-${cleaned.substring(7, 9)}-${cleaned.substring(9)}`;
    }

    return phoneNumber;
  };

  const contactInfo = [
    {
      id: 1,
      icon: MailIcon,
      label: "Email",
      value: "reutov.avtoshkola@mail.ru",
      href: "mailto:reutov.avtoshkola@mail.ru",
    },
    {
      id: 2,
      icon: TelegramIcon,
      label: "@rsvmTG",
      value: "Мы в телеграм",
      href: "https://t.me/+79255428489",
    },
    {
      id: 3,
      icon: MaxIcon,
      label: "@rsvmMAX",
      value: "Мы в МАКС",
      href: "https://max.ru/u/f9LHodD0cOLICzbWbnVUr9m-9yJ3I-eNgieqljehB1FOTLtz7gNZ0Rvtqzo",
    },
  ];

  const handleCallOrder = () => {
    if (onOpenPopup) {
      onOpenPopup();
    }
  };

  if (loading) {
    return (
      <footer className={styles.footer}>
        <div className={styles.footer__content}>
          <div className={styles.loading}>
            <div>Загрузка контактов...</div>
            <div>Загрузка адресов филиалов...</div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className={styles.footer}>
      <div className={styles.footer__content}>
        <div className={styles.footer__contact}>
          <div className={styles.footer__contact__bg} />
          <div className={styles.contactCard}>
            <h2 className={styles.contactCard__title}>
              ХОЧЕШЬ НАЧАТЬ ОБУЧЕНИЕ <br /> ИЛИ ОСТАЛИСЬ ВОПРОСЫ?
            </h2>

            {error && (
              <div className={styles.message}>
                <p className={styles.message__text}>{error}</p>
                <p className={styles.message__help}>
                  Пожалуйста, попробуйте обновить страницу позже.
                </p>
              </div>
            )}

            {!error && contacts.length > 0 ? (
              <>
                <div className={styles.contactCard__phones}>
                  {contacts.map((contact) => (
                    <a
                      key={contact.id}
                      href={contact.href}
                      className={styles.phoneLink}
                      aria-label={`Позвонить по телефону: ${contact.value}`}
                    >
                      <PhoneIcon
                        className={styles.phoneLink__icon}
                        aria-hidden="true"
                      />
                      <span className={styles.phoneLink__number}>
                        {contact.value}
                      </span>
                    </a>
                  ))}
                  <button
                    className={styles.contactCard__callBtn}
                    onClick={handleCallOrder}
                    type="button"
                  >
                    Заказать звонок
                  </button>
                </div>
                <h3 className={styles.contactCard__subtitle}>НАПИШИ НАМ!</h3>

                <div className={styles.contactCard__info}>
                  {contactInfo.map((info) => {
                    const Icon = info.icon;
                    return (
                      <a
                        key={info.id}
                        href={info.href}
                        className={styles.infoLink}
                        aria-label={`Написать в ${info.label}: ${info.value}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon
                          className={styles.infoLink__icon}
                          aria-hidden="true"
                        />
                        <span className={styles.infoLink__text}>
                          {info.value}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </>
            ) : !error && contacts.length === 0 ? (
              <div className={styles.message}>
                <p className={styles.message__text}>
                  Контактная информация временно недоступна
                </p>
              </div>
            ) : null}
          </div>
        </div>

        <div className={styles.footer__locationsWrapper}>
          <div className={styles.footer__locations}>
            <h3 className={styles.footer__locationsTitle}>КАК НАС НАЙТИ</h3>

            {locationsLoading ? (
              <div className={styles.message}>
                <p className={styles.message__text}>
                  Загрузка адресов филиалов...
                </p>
              </div>
            ) : locationsError ? (
              <div className={styles.message}>
                <p
                  className={`${styles.message__text} ${styles.message__error}`}
                >
                  {locationsError}
                </p>
                <p className={styles.message__help}>
                  Пожалуйста, попробуйте обновить страницу позже.
                </p>
              </div>
            ) : locations.length > 0 ? (
              <div className={styles.locationsGrid}>
                {locations.map((location) => (
                  <LocationCard key={location.id} location={location} />
                ))}
              </div>
            ) : (
              <div className={styles.message}>
                <p className={styles.message__text}>
                  Информация о филиалах временно недоступна
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
